/**
 * Shared currency formatting composable.
 *
 * Formats a numeric (or pre-formatted string) price value using
 * the browser's Intl.NumberFormat API, based on the provided ISO
 * currency code (e.g. 'SEK', 'EUR', 'NOK').
 *
 * This is intentionally separate from window.VendreCurrencyFormatter,
 * which assumes the input is in the shop's base currency (SEK) and
 * applies a conversion factor. Use THIS function for prices that are
 * already in the correct target currency – e.g. prices from the ERP
 * pull API, which are returned in the market's native currency.
 */

const CURRENCY_LOCALE_MAP = {
  SEK: 'sv-SE',
  NOK: 'nb-NO',
  EUR: 'de-DE',
  DKK: 'da-DK',
  GBP: 'en-GB',
  USD: 'en-US',
};

function getLocale(currencyCode) {
  return CURRENCY_LOCALE_MAP[currencyCode] ?? 'en-US';
}

export function useCurrencyFormatter(currencyCode) {
  const locale = getLocale(currencyCode);

  function formatPrice(value) {
    if (typeof value === 'string') {
      value = value
        .replace(/[^\d,.-]/g, '')
        .replace(/\s/g, '')
        .replace(',', '.');
    }

    const num = parseFloat(value);
    if (isNaN(num)) return value;

    if (num >= 1 || num === 0) {
      const rounded = Math.ceil(num * 100) / 100;
      return rounded.toLocaleString(locale, {
        style: 'currency',
        currency: currencyCode,
      });
    } else {
      // For sub-unit values, preserve full decimal precision
      const str = num.toFixed(4);
      const [whole, dec] = str.split('.');
      const currencySymbol = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode,
      }).format(0).replace(/[\d\s.,]/g, '').trim();
      const formatted = `${Number(whole).toLocaleString(locale)},${dec}`;
      return `${formatted} ${currencySymbol}`;
    }
  }

  return { formatPrice };
}
